import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Bar } from "react-chartjs-2";
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from "chart.js";
import { Search } from "lucide-react";
import { getDemoGraphicsUSerData } from "../../Action/actionFile";
import { CustomButton, CustomDropdowns, CustomEmptyState, CustomPageLayout } from "../../Component/customComponents";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const STATUS_LIST = [
  { _id: 1, name: "Active" },
  { _id: 9, name: "Inactive" },
];
const USER_LIST = [
  { _id: 1, name: "FREE" },
  { _id: 2, name: "PAID" },
];

// same labels as before
const labelOf = (id) => (id === true ? "TRUE" : id === false ? "FALSE" : id === 1 ? "ACTIVE" : id === 9 ? "INACTIVE" : id ?? "NULL");

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, title: { display: false } },
  scales: { y: { beginAtZero: true } },
};

/** one chart + its counts; wide when there are more than 5 bars (as before) */
const ChartSection = ({ title, data }) => {
  if (!data?.length) return null;
  const chartData = {
    labels: data.map((d) => labelOf(d._id)),
    datasets: [{ label: "Count", data: data.map((d) => d.count || 0), backgroundColor: "rgba(54, 162, 235, 0.6)" }],
  };
  const total = data.reduce((t, d) => t + (Number(d.count) || 0), 0);
  return (
    <div className={data.length > 5 ? "col-12" : "col-xl-4 col-md-6 col-12"}>
      <section className="bg-white border rounded-3 shadow-sm h-100 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <h6 className="mb-0 fw-bold">{title}</h6>
          <span className="small text-muted">{total.toLocaleString("en-IN")} users</span>
        </div>
        <div className="p-2" style={{ height: 320 }}>
          <Bar data={chartData} options={CHART_OPTIONS} />
        </div>
        <div className="overflow-auto px-3 pb-2" style={{ maxHeight: 220 }}>
          <table className="table table-sm mb-0 small">
            <thead className="sticky-top bg-white">
              <tr>
                <th className="text-muted fw-semibold">S.No</th>
                <th className="text-muted fw-semibold">Name</th>
                <th className="text-muted fw-semibold text-end">Count</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{labelOf(d._id)}</td>
                  <td className="text-end fw-semibold">{(Number(d.count) || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

function DemoGraphicsData() {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("");
  const [activePlan, setActivePlan] = useState("");
  const [tried, setTried] = useState(false);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    setTried(true);
    if (!status || !activePlan) return;
    setLoading(true);
    dispatch(
      getDemoGraphicsUSerData(
        { hasActivePlan: activePlan._id === 2, status: status._id }, // same request as before
        (res) => {
          setSections(res?.data ?? {});
          setLoading(false);
        },
        () => {
          setSections({});
          setLoading(false);
        },
      ),
    );
  };

  const entries = Object.entries(sections || {}).filter(([, v]) => Array.isArray(v) && v.length);

  return (
    <CustomPageLayout title="DEMOGRAPHIC DATA ANALYSIS">
      <div className="d-flex flex-column gap-3">
        <form className="bg-white border rounded-3 shadow-sm p-3" onSubmit={onSearch} noValidate>
          <div className="row g-3 align-items-start">
            <CustomDropdowns
              colClass="col-md-4 col-12"
              label="Status"
              isRequired
              options={STATUS_LIST}
              value={status}
              onChange={setStatus}
              search={false}
              error={tried && !status ? "Select a status" : ""}
            />
            <CustomDropdowns
              colClass="col-md-4 col-12"
              label="User"
              isRequired
              options={USER_LIST}
              value={activePlan}
              onChange={setActivePlan}
              search={false}
              error={tried && !activePlan ? "Select a user type" : ""}
            />
            <div className="col-md-4 col-12 d-grid align-self-end">
              <CustomButton type="submit" icon={<Search size={15} />} loading={loading}>
                Search
              </CustomButton>
            </div>
          </div>
        </form>

        {sections === null ? (
          <CustomEmptyState title="Pick a status and user type" description="Then click Search to see the demographic charts." />
        ) : entries.length === 0 ? (
          <CustomEmptyState title="No data" description="Nothing for this selection." />
        ) : (
          <div className="row g-3">
            {entries.map(([key, value]) => (
              <ChartSection key={key} title={key.replace(/([A-Z])/g, " $1").toUpperCase()} data={value} />
            ))}
          </div>
        )}
      </div>
    </CustomPageLayout>
  );
}

export default DemoGraphicsData;
